// Author: Ozy
// Cart Image Viewer with Metadata
// Opens uploaded images in a modal with technical details
//
// FUTURE FEATURE: Image Validation & Crop Tool
// When product metafields are added (digital_width, digital_height, digital_dpi):
// 1. Validates uploaded image meets minimum resolution requirements
// 2. Shows aspect ratio overlay frame for crop preview
// 3. Calculates: min_pixels = digital_width * digital_height * (digital_dpi / 72)^2
// 4. Displays pass/fail validation in "Product Requirements" section
// Note: Uploaded image embedded DPI is ignored - only pixel dimensions matter

class CartImageViewer {
  constructor() {
    this.modal = null;
    this.init();
  }

  init() {
    console.log('[Image Viewer] Initializing...');
    
    // Listen for viewer links (cart page)
    document.addEventListener('click', (e) => {
      const link = e.target.closest('.cart-file-preview-link');
      if (link) {
        console.log('[Image Viewer] Cart link clicked');
        e.preventDefault();
        const imageUrl = link.getAttribute('data-image-url');
        const originalUrl = link.getAttribute('data-original-url');
        const editData = link.getAttribute('data-edit-data');
        const lineKey = link.getAttribute('data-line-key');
        
        console.log('[Image Viewer] Image URL from cart:', imageUrl);
        console.log('[Image Viewer] Original URL:', originalUrl);
        console.log('[Image Viewer] Edit data:', editData);
        console.log('[Image Viewer] Line key:', lineKey);
        
        if (imageUrl) {
          this.openViewer(imageUrl, null, false, originalUrl, editData, lineKey);
        }
      }
    });

    // Listen for custom event from product page
    document.addEventListener('open-image-viewer', (e) => {
      console.log('[Image Viewer] Custom event received:', e.detail);
      if (e.detail && e.detail.imageUrl) {
        // Pass requirements, editData (which may have swapped requirements), originalUrl
        this.openViewer(
          e.detail.imageUrl, 
          e.detail.requirements, 
          e.detail.hasRequirements,
          e.detail.originalUrl || null,
          e.detail.editData || null
        );
      }
    });

    // Create modal container
    this.createModal();
    console.log('[Image Viewer] Initialized successfully');
  }

  detectDarkMode() {
    console.log('[Image Viewer] Detecting theme mode...');
    
    // Check the site-wide color mode setting from Shopify theme customizer
    const siteColorMode = window.themeSettings?.siteColorMode || document.documentElement.getAttribute('data-color-mode') || 'system';
    console.log('[Image Viewer] Site color mode setting:', siteColorMode);
    
    // Check data-theme attribute applied by dark-mode.js
    const currentTheme = document.documentElement.getAttribute('data-theme');
    if (currentTheme) {
      console.log('[Image Viewer] ✓ Using site-wide theme:', currentTheme);
      return currentTheme === 'dark';
    }
    
    // Fallback to explicit setting if dark-mode.js hasn't run yet
    if (siteColorMode === 'light') {
      console.log('[Image Viewer] ✓ Light mode (site setting)');
      return false;
    }
    
    if (siteColorMode === 'dark') {
      console.log('[Image Viewer] ✓ Dark mode (site setting)');
      return true;
    }
    
    // If setting is "system", auto-detect
    console.log('[Image Viewer] Auto-detecting (system preference)...');
    
    // Method 1: Check for explicit dark mode classes on html or body
    const hasClass = document.documentElement.classList.contains('dark-mode') || 
        document.body.classList.contains('dark-mode') ||
        document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark');
    
    if (hasClass) {
      console.log('[Image Viewer] ✓ Dark mode detected via CSS class');
      return true;
    }

    // Method 2: Check for common Shopify theme dark mode attributes
    const htmlTheme = document.documentElement.getAttribute('data-theme') || document.documentElement.getAttribute('data-color-scheme');
    const bodyTheme = document.body.getAttribute('data-theme') || document.body.getAttribute('data-color-scheme');
    
    if (htmlTheme === 'dark' || bodyTheme === 'dark') {
      console.log(`[Image Viewer] ✓ Dark mode detected via data attribute: ${htmlTheme || bodyTheme}`);
      return true;
    }

    // Method 3: Check background color - if it's dark, assume dark mode
    const bodyBg = window.getComputedStyle(document.body).backgroundColor;
    console.log('[Image Viewer] Body background color:', bodyBg);
    
    if (bodyBg) {
      const rgb = bodyBg.match(/\d+/g);
      if (rgb && rgb.length >= 3) {
        const luminance = (parseInt(rgb[0]) + parseInt(rgb[1]) + parseInt(rgb[2])) / 3;
        console.log('[Image Viewer] Body luminance:', luminance, '(< 128 = dark)');
        
        if (luminance < 128) {
          console.log('[Image Viewer] ✓ Dark mode detected via background color');
          return true;
        }
      }
    }

    // Method 4: Check for prefers-color-scheme as last resort
    const prefersMatch = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    console.log('[Image Viewer] OS prefers dark mode:', prefersMatch);
    
    if (prefersMatch) {
      // Double-check: only use OS preference if we couldn't determine from the page itself
      const htmlBg = window.getComputedStyle(document.documentElement).backgroundColor;
      if (htmlBg && htmlBg !== 'rgba(0, 0, 0, 0)' && htmlBg !== 'transparent') {
        const rgb = htmlBg.match(/\d+/g);
        if (rgb && rgb.length >= 3) {
          const luminance = (parseInt(rgb[0]) + parseInt(rgb[1]) + parseInt(rgb[2])) / 3;
          if (luminance < 128) {
            console.log('[Image Viewer] ✓ Dark mode confirmed via HTML background');
            return true;
          }
        }
      }
      
      // Check if body is clearly light
      const bodyRgb = bodyBg ? bodyBg.match(/\d+/g) : null;
      if (bodyRgb && bodyRgb.length >= 3) {
        const bodyLuminance = (parseInt(bodyRgb[0]) + parseInt(bodyRgb[1]) + parseInt(bodyRgb[2])) / 3;
        if (bodyLuminance >= 200) {
          console.log('[Image Viewer] ✗ Body is clearly light (luminance:', bodyLuminance, '), ignoring OS preference');
          return false;
        }
      }
      
      console.log('[Image Viewer] ✓ Using OS preference (dark mode)');
      return true;
    }

    // Default to light mode
    console.log('[Image Viewer] ✗ Defaulting to light mode');
    return false;
  }

  createModal() {
    // Don't detect theme at page load - we'll do it when modal opens
    const isDarkMode = false; // Placeholder, will be updated when opened
    
    // Theme colors
    const colors = isDarkMode ? {
      backdrop: 'rgba(0, 0, 0, 0.95)',
      modalBg: '#2a2a2a',
      modalBorder: '#444',
      text: '#f5f5f5',
      textSecondary: '#bbb',
      closeBtn: '#2a2a2a',
      closeBtnHover: '#3a3a3a',
      closeBtnText: '#ffffff',
      imgBg: '#1a1a1a',
      metaBg: '#1a1a1a'
    } : {
      backdrop: 'rgba(0, 0, 0, 0.85)',
      modalBg: '#ffffff',
      modalBorder: '#e0e0e0',
      text: '#1a1a1a',
      textSecondary: '#666',
      closeBtn: '#f5f5f5',
      closeBtnHover: '#e0e0e0',
      closeBtnText: '#1a1a1a',
      imgBg: '#f5f5f5',
      metaBg: '#f9f9f9'
    };
    
    this.modal = document.createElement('div');
    this.modal.id = 'cart-image-viewer-modal';
    this.modal.style.cssText = `
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${colors.backdrop};
      z-index: 10000;
      overflow: auto;
      padding: 2rem;
    `;

    this.modal.innerHTML = `
      <div style="max-width: 1400px; margin: 3rem auto; position: relative; display: flex; align-items: center; min-height: calc(100vh - 6rem);">
        <div style="width: 100%;">
          <button id="close-image-viewer" style="
            position: absolute;
            top: -15px;
            right: -15px;
            background: ${colors.closeBtn};
            color: ${colors.closeBtnText};
            border: 2px solid ${colors.modalBorder};
            width: 50px;
            height: 50px;
            border-radius: 50%;
            cursor: pointer;
            font-size: 32px;
            line-height: 1;
            box-shadow: 0 4px 12px rgba(0,0,0,0.5);
            z-index: 10;
            font-weight: 300;
            transition: all 0.2s;
          " onmouseover="this.style.background='${colors.closeBtnHover}'" onmouseout="this.style.background='${colors.closeBtn}'">&times;</button>
          
          <div style="background: ${colors.modalBg}; color: ${colors.text}; border-radius: 12px; padding: 2.5rem; box-shadow: 0 8px 32px rgba(0,0,0,0.8); border: 1px solid ${colors.modalBorder};">
            <h2 style="margin: 0 0 2rem 0; font-size: 2.25rem; font-weight: 600; color: ${colors.text};">Uploaded Image Details</h2>
          
          <div style="display: flex; gap: 2.5rem; flex-wrap: wrap;">
            <div style="flex: 1; min-width: 350px;">
              <div id="viewer-image-container" style="
                background: ${colors.imgBg};
                border-radius: 8px;
                padding: 1.5rem;
                text-align: center;
                min-height: 500px;
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
                border: 2px solid ${colors.modalBorder};
              ">
                <img id="viewer-image" style="
                  max-width: 100%;
                  max-height: 650px;
                  height: auto;
                  border-radius: 6px;
                  box-shadow: 0 4px 16px rgba(0,0,0,0.3);
                " />
                <canvas id="viewer-overlay-canvas" style="
                  position: absolute;
                  pointer-events: none;
                  display: none;
                "></canvas>
              </div>
              <div style="margin-top: 1.25rem; display: flex; justify-content: center; gap: 1.5rem; flex-wrap: wrap;">
                <a id="viewer-download-link" href="#" download style="color: ${colors.text}; text-decoration: underline; font-weight: 500; font-size: 1.1rem;">Download original</a>
                <button type="button" id="viewer-edit-btn" style="
                  display: inline-flex;
                  align-items: center;
                  gap: 0.5rem;
                  padding: 0.5rem 1rem;
                  background: ${colors.closeBtn};
                  border: 1px solid ${colors.modalBorder};
                  border-radius: 6px;
                  color: ${colors.text};
                  font-size: 1rem;
                  font-weight: 500;
                  cursor: pointer;
                  transition: all 0.2s;
                ">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M11.5 2.5L13.5 4.5M1 15L1.5 12.5L12 2L14 4L3.5 14.5L1 15Z" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Edit Image
                </button>
              </div>
            </div>
            
            <div style="flex: 0 0 380px;">
              <h3 style="margin: 0 0 1.5rem 0; font-size: 1.5rem; font-weight: 600; color: ${colors.text};">Image Information</h3>
              <div id="viewer-metadata" style="
                background: ${colors.metaBg};
                color: ${colors.text};
                border-radius: 8px;
                padding: 1.5rem;
                font-size: 1.15rem;
                line-height: 2;
                border: 1px solid ${colors.modalBorder};
              ">
                <div style="margin-bottom: 0.75rem;">
                  <strong>Dimensions:</strong><br>
                  <span id="meta-dimensions">Loading...</span>
                </div>
                <div style="margin-bottom: 0.75rem;">
                  <strong>File Size:</strong><br>
                  <span id="meta-filesize">Loading...</span>
                </div>
                <div style="margin-bottom: 0.75rem;">
                  <strong>Color Space:</strong><br>
                  <span id="meta-colorspace">Unknown</span>
                </div>
                <div style="margin-bottom: 0.75rem;">
                  <strong id="aspect-ratio-label">Aspect Ratio:</strong><br>
                  <span id="meta-aspect">Loading...</span>
                </div>
                <div style="margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid ${colors.modalBorder};">
                  <strong style="color: #16a34a; font-size: 1.3rem;">✓ Checks Passed</strong>
                  <div id="meta-validation-checks" style="margin-top: 1rem; font-size: 1.1rem; line-height: 2;">
                    <div id="resolution-check" style="color: ${colors.text};">
                      <span style="color: #16a34a; font-size: 1.2rem; margin-right: 0.5rem;">✓</span>
                      <span id="meta-dimensions-check">Loading...</span>
                    </div>
                    <div id="aspect-check" style="color: ${colors.text}; margin-top: 0.5rem;">
                      <span style="color: #16a34a; font-size: 1.2rem; margin-right: 0.5rem;">✓</span>
                      <span id="meta-aspect-check">Loading...</span>
                    </div>
                  </div>
                </div>
                <div id="meta-validation-check" style="margin-top: 1rem; padding-top: 1rem; border-top: 1px solid ${colors.modalBorder}; display: none;">
                  <strong style="color: ${colors.text};">Product Requirements:</strong><br>
                  <div id="meta-requirements" style="margin-top: 0.5rem; font-size: 1.05rem;"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    `;

    document.body.appendChild(this.modal);

    // Close button
    this.modal.querySelector('#close-image-viewer').addEventListener('click', () => this.closeViewer());
    
    // Edit button - opens image editor
    this.modal.querySelector('#viewer-edit-btn').addEventListener('click', () => this.editImage());
    
    // Close on backdrop click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.closeViewer();
      }
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.style.display === 'block') {
        this.closeViewer();
      }
    });
  }

  async openViewer(imageUrl, requirements = null, hasRequirements = false, originalUrl = null, editData = null, lineKey = null) {
    try {
      console.log('[Image Viewer] Opening viewer for:', imageUrl);
      console.log('[Image Viewer] Requirements passed:', requirements);
      console.log('[Image Viewer] Has requirements passed:', hasRequirements);
      console.log('[Image Viewer] Original URL:', originalUrl);
      console.log('[Image Viewer] Edit data:', editData);
      console.log('[Image Viewer] Line key:', lineKey);
      
      // Store current image URL and related data for edit functionality
      this.currentImageUrl = imageUrl;
      this.currentOriginalUrl = originalUrl || imageUrl; // Fall back to imageUrl if no original
      this.currentLineKey = lineKey;
      
      // Parse edit data if it's a string
      if (editData) {
        try {
          this.currentEditData = typeof editData === 'string' ? JSON.parse(editData) : editData;
        } catch (e) {
          console.error('[Image Viewer] Failed to parse edit data:', e);
          this.currentEditData = null;
        }
      } else {
        this.currentEditData = null;
      }
      
      // Use requirements from various sources
      // PRIORITY: editData.edits.requirements > passed requirements > window.imageRequirements
      // EditData takes priority because it contains what the user actually cropped to,
      // including any crop orientation rotation (4x6 -> 6x4)
      // Note: requirements are stored inside .edits object when saved from editor
      const editDataReqs = this.currentEditData?.edits?.requirements || this.currentEditData?.requirements;
      if (editDataReqs) {
        // Requirements stored in edit data (from previous edit session) - includes rotation
        this.currentRequirements = editDataReqs;
        this.hasRequirements = true;
        console.log('[Image Viewer] Using requirements from edit data (may include rotation):', this.currentRequirements);
      } else if (requirements && hasRequirements) {
        this.currentRequirements = requirements;
        this.hasRequirements = true;
        console.log('[Image Viewer] Using passed requirements:', this.currentRequirements);
      } else if (window.imageRequirements?.hasRequirements && window.imageRequirements.slots?.length > 0) {
        // Fall back to first slot's requirements from window object
        this.currentRequirements = window.imageRequirements.slots[0];
        this.hasRequirements = true;
        console.log('[Image Viewer] Using window.imageRequirements fallback:', this.currentRequirements);
      } else {
        this.currentRequirements = null;
        this.hasRequirements = false;
        console.log('[Image Viewer] No requirements available');
      }
      
      // Detect theme mode RIGHT NOW (not at page load)
      const isDarkMode = this.detectDarkMode();
      
      // Update modal colors based on current theme
      try {
        this.updateModalTheme(isDarkMode);
      } catch (themeError) {
        console.error('[Image Viewer] Theme update error (non-fatal):', themeError);
        // Continue anyway - modal will use default colors
      }
      
      this.modal.style.display = 'block';
      document.body.style.overflow = 'hidden';

      const img = this.modal.querySelector('#viewer-image');
      const downloadLink = this.modal.querySelector('#viewer-download-link');

      if (!img || !downloadLink) {
        console.error('[Image Viewer] Modal elements not found!');
        return;
      }

      // Set image source
      img.src = imageUrl;
      downloadLink.href = imageUrl;

      // Load and analyze image
      img.onload = () => {
        this.analyzeImage(img, imageUrl, this.currentRequirements, this.hasRequirements);
      };

      img.onerror = () => {
        const metadataDiv = this.modal.querySelector('#viewer-metadata');
        if (metadataDiv) {
          metadataDiv.innerHTML = `
            <p style="color: #ef4444; font-size: 1.15rem; font-weight: 500;">Failed to load image. The file may have been deleted or is temporarily unavailable.</p>
          `;
        }
      };
      
      console.log('[Image Viewer] Modal opened successfully');
    } catch (error) {
      console.error('[Image Viewer] Failed to open viewer:', error);
    }
  }

  updateModalTheme(isDarkMode) {
    this.modal.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    
    const colors = isDarkMode ? {
      backdrop: 'rgba(0, 0, 0, 0.95)',
      modalBg: '#2a2a2a',
      modalBorder: '#444',
      text: '#f5f5f5',
      imgBg: '#1a1a1a',
      metaBg: '#1a1a1a',
      closeBtn: '#2a2a2a',
      closeBtnText: '#ffffff'
    } : {
      backdrop: 'rgba(0, 0, 0, 0.85)',
      modalBg: '#ffffff',
      modalBorder: '#e0e0e0',
      text: '#1a1a1a',
      imgBg: '#f5f5f5',
      metaBg: '#f9f9f9',
      closeBtn: '#f5f5f5',
      closeBtnText: '#1a1a1a'
    };

    console.log('[Image Viewer] Applying theme:', isDarkMode ? 'DARK' : 'LIGHT', colors);

    // ONLY update specific elements, don't touch everything
    this.modal.style.background = colors.backdrop;

    // Main content container (the white/dark panel)
    const mainPanel = this.modal.querySelector('div > div > div[style*="border-radius"]');
    if (mainPanel) {
      // Parse existing style and update only background, color, border
      const style = mainPanel.getAttribute('style');
      const newStyle = style
        .replace(/background:\s*[^;]+/gi, `background: ${colors.modalBg}`)
        .replace(/color:\s*[^;]+/gi, `color: ${colors.text}`)
        .replace(/border:\s*[^;]+/gi, `border: 1px solid ${colors.modalBorder}`);
      mainPanel.setAttribute('style', newStyle);
      console.log('[Image Viewer] Updated main panel');
    }

    // Headings
    this.modal.querySelectorAll('h2, h3').forEach(h => {
      const style = h.getAttribute('style');
      if (style) {
        h.setAttribute('style', style.replace(/color:\s*[^;]+/gi, `color: ${colors.text}`));
      }
    });

    // Image container background
    const imgContainer = this.modal.querySelector('#viewer-image-container');
    if (imgContainer) {
      const style = imgContainer.getAttribute('style');
      const newStyle = style
        .replace(/background:\s*[^;]+/gi, `background: ${colors.imgBg}`)
        .replace(/border:\s*[^;]+/gi, `border: 2px solid ${colors.modalBorder}`);
      imgContainer.setAttribute('style', newStyle);
    }

    // Metadata panel
    const metadataDiv = this.modal.querySelector('#viewer-metadata');
    if (metadataDiv) {
      const style = metadataDiv.getAttribute('style');
      const newStyle = style
        .replace(/background:\s*[^;]+/gi, `background: ${colors.metaBg}`)
        .replace(/color:\s*[^;]+/gi, `color: ${colors.text}`)
        .replace(/border:\s*[^;]+/gi, `border: 1px solid ${colors.modalBorder}`);
      metadataDiv.setAttribute('style', newStyle);
    }

    // Close button
    const closeBtn = this.modal.querySelector('#close-image-viewer');
    if (closeBtn) {
      const style = closeBtn.getAttribute('style');
      const newStyle = style
        .replace(/background:\s*[^;]+/gi, `background: ${colors.closeBtn}`)
        .replace(/color:\s*[^;]+/gi, `color: ${colors.closeBtnText}`)
        .replace(/border:\s*[^;]+/gi, `border: 2px solid ${colors.modalBorder}`);
      closeBtn.setAttribute('style', newStyle);
    }

    console.log('[Image Viewer] Theme applied successfully');
  }

  closeViewer() {
    this.modal.style.display = 'none';
    document.body.style.overflow = '';
  }

  editImage() {
    // Use original URL for editing, not the edited URL
    const imageToEdit = this.currentOriginalUrl || this.currentImageUrl;
    
    if (!imageToEdit) {
      console.error('[Image Viewer] No image URL available for editing');
      return;
    }
    
    console.log('[Image Viewer] Opening editor for ORIGINAL:', imageToEdit);
    console.log('[Image Viewer] With requirements:', this.currentRequirements);
    console.log('[Image Viewer] With edit data:', this.currentEditData);
    console.log('[Image Viewer] Cart line key:', this.currentLineKey);
    
    // Save product form state before navigating (if on product page)
    this.saveProductFormState();
    
    // Build editor URL with original image
    const params = new URLSearchParams({
      image: imageToEdit,
      return: window.location.href
    });
    
    // Add requirements if available
    if (this.currentRequirements) {
      params.set('requirements', JSON.stringify(this.currentRequirements));
    }
    
    // Add edit data to restore state
    if (this.currentEditData) {
      params.set('editData', typeof this.currentEditData === 'string' 
        ? this.currentEditData 
        : JSON.stringify(this.currentEditData));
    }
    
    // Add cart line key if editing from cart
    if (this.currentLineKey) {
      params.set('cartLineKey', this.currentLineKey);
    }
    
    // Navigate to editor page
    const editorUrl = `/pages/image-editor?${params.toString()}`;
    window.location.href = editorUrl;
  }

  /**
   * Save product form state before navigating to editor
   * Only applicable when viewing from product page (not cart)
   */
  saveProductFormState() {
    // Try to find a product form on the page
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) {
      console.log('[Image Viewer] No product form found, skipping form state save');
      return;
    }
    
    // Try to get product handle from the page
    const productHandle = document.querySelector('[data-product-handle]')?.dataset.productHandle ||
                          window.location.pathname.split('/products/')[1]?.split('/')[0]?.split('?')[0];
    
    if (!productHandle) {
      console.log('[Image Viewer] No product handle found, skipping form state save');
      return;
    }
    
    const formState = {};
    
    // Save all radio button selections
    const radioGroups = new Set();
    form.querySelectorAll('input[type="radio"]').forEach(radio => {
      radioGroups.add(radio.name);
    });
    radioGroups.forEach(name => {
      const checked = form.querySelector(`input[type="radio"][name="${name}"]:checked`);
      if (checked) {
        formState[`radio_${name}`] = checked.value;
      }
    });
    
    // Save all checkbox states
    form.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      if (checkbox.name && !checkbox.name.startsWith('properties[_')) {
        formState[`checkbox_${checkbox.name}`] = checkbox.checked;
      }
    });
    
    // Save all text inputs (except hidden ones for image data)
    form.querySelectorAll('input[type="text"], input[type="number"], input[type="email"]').forEach(input => {
      if (input.name && !input.name.startsWith('properties[_') && input.value) {
        formState[`text_${input.name}`] = input.value;
      }
    });
    
    // Save all textareas
    form.querySelectorAll('textarea').forEach(textarea => {
      if (textarea.name && textarea.value) {
        formState[`textarea_${textarea.name}`] = textarea.value;
      }
    });
    
    // Save all select dropdowns
    form.querySelectorAll('select').forEach(select => {
      if (select.name && select.value) {
        formState[`select_${select.name}`] = select.value;
      }
    });
    
    // Store with product handle as key
    const stateKey = `productFormState_${productHandle}`;
    sessionStorage.setItem(stateKey, JSON.stringify(formState));
    console.log('[Image Viewer] Saved product form state:', formState);
  }

  async analyzeImage(img, imageUrl, requirements = null, hasRequirements = false) {
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    
    // Calculate user-friendly aspect ratio
    const aspectRatioDisplay = this.getDisplayAspectRatio(width, height);
    
    // Update dimensions
    document.getElementById('meta-dimensions').textContent = `${width} × ${height} pixels`;
    document.getElementById('meta-aspect').textContent = aspectRatioDisplay;

    // Validation based on requirements
    const megapixels = (width * height) / 1000000;
    let resolutionPassed = megapixels >= 1.5; // Default fallback
    let aspectPassed = true;
    let resolutionText = `Resolution: ${width} × ${height} pixels (${megapixels.toFixed(2)} MP)`;
    let aspectText = `Aspect Ratio: ${aspectRatioDisplay}`;
    
    if (hasRequirements && requirements) {
      console.log('[Image Viewer] Validating against requirements:', requirements);
      
      // Check resolution
      const minWidth = requirements.minPixelWidth || 0;
      const minHeight = requirements.minPixelHeight || 0;
      resolutionPassed = width >= minWidth && height >= minHeight;
      
      if (!resolutionPassed) {
        resolutionText = `Resolution: ${width} × ${height} (need ${minWidth} × ${minHeight})`;
      }
      
      // Check aspect ratio
      const requiredAspect = requirements.aspectRatio || (requirements.widthInches / requirements.heightInches);
      const imageAspect = width / height;
      const aspectTolerance = 0.1; // 10% tolerance
      aspectPassed = Math.abs(imageAspect - requiredAspect) / requiredAspect <= aspectTolerance;
      
      if (!aspectPassed) {
        const reqAspectDisplay = this.getDisplayAspectRatio(
          requirements.widthInches * 100, 
          requirements.heightInches * 100
        );
        aspectText = `Aspect Ratio: ${aspectRatioDisplay} (need ~${reqAspectDisplay})`;
      }
      
      // Show requirements section
      const reqSection = document.getElementById('meta-validation-check');
      const reqDiv = document.getElementById('meta-requirements');
      if (reqSection && reqDiv) {
        reqSection.style.display = 'block';
        reqDiv.innerHTML = `
          <div>Required: ${requirements.widthInches}" × ${requirements.heightInches}" at ${requirements.dpi} DPI</div>
          <div>Minimum: ${minWidth} × ${minHeight} pixels</div>
        `;
      }
    }
    
    // Update checks UI
    const checksHeader = this.modal.querySelector('#viewer-metadata strong');
    const resolutionCheckEl = document.getElementById('resolution-check');
    const aspectCheckEl = document.getElementById('aspect-check');
    
    const allPassed = resolutionPassed && aspectPassed;
    
    // Update header
    if (checksHeader) {
      if (allPassed) {
        checksHeader.innerHTML = '<span style="color: #16a34a;">✓ Checks Passed</span>';
      } else {
        checksHeader.innerHTML = '<span style="color: #f59e0b;">⚠ Issues Found</span>';
      }
    }
    
    // Update resolution check
    if (resolutionCheckEl) {
      const icon = resolutionPassed 
        ? '<span style="color: #16a34a; font-size: 1.2rem; margin-right: 0.5rem;">✓</span>'
        : '<span style="color: #ef4444; font-size: 1.2rem; margin-right: 0.5rem;">✗</span>';
      resolutionCheckEl.innerHTML = `${icon}<span id="meta-dimensions-check">${resolutionText}</span>`;
    }
    
    // Update aspect check
    if (aspectCheckEl) {
      const icon = aspectPassed 
        ? '<span style="color: #16a34a; font-size: 1.2rem; margin-right: 0.5rem;">✓</span>'
        : '<span style="color: #f59e0b; font-size: 1.2rem; margin-right: 0.5rem;">⚠</span>';
      aspectCheckEl.innerHTML = `${icon}<span id="meta-aspect-check">${aspectText}</span>`;
    }

    // Get file size via fetch
    try {
      const response = await fetch(imageUrl, { method: 'HEAD' });
      const fileSize = response.headers.get('content-length');
      
      if (fileSize) {
        document.getElementById('meta-filesize').textContent = this.formatFileSize(parseInt(fileSize));
      } else {
        document.getElementById('meta-filesize').textContent = 'Unknown';
      }
    } catch (error) {
      console.error('Error fetching image metadata:', error);
      document.getElementById('meta-filesize').textContent = 'Unable to retrieve';
    }

    // Color space (browsers typically convert to sRGB)
    document.getElementById('meta-colorspace').textContent = 'sRGB (standard)';
  }

  // Future: Validate image against product requirements
  validateImageRequirements(width, height, productMetadata) {
    // TODO: When product metafields are available (digital_width, digital_height, digital_dpi)
    // Calculate minimum required pixel resolution using product's digital dimensions & DPI
    // Formula: min_pixels = digital_width * digital_height * (digital_dpi / 72)^2
    // Compare uploaded image pixel dimensions (width × height) against minimum
    // Display validation result in #meta-validation-check
    // Draw aspect ratio overlay on canvas
    
    if (!productMetadata) return;
    
    const requiredWidth = productMetadata.digital_width;
    const requiredHeight = productMetadata.digital_height;
    const requiredDPI = productMetadata.digital_dpi;
    
    // Calculate minimum pixels needed
    const dpiMultiplier = Math.pow(requiredDPI / 72, 2);
    const minWidth = requiredWidth * dpiMultiplier;
    const minHeight = requiredHeight * dpiMultiplier;
    
    // Check if image meets requirements
    const meetsRequirements = width >= minWidth && height >= minHeight;
    
    // Display validation result
    const validationContainer = document.getElementById('meta-validation-check');
    validationContainer.style.display = 'block';
    
    const requirementsDiv = document.getElementById('meta-requirements');
    requirementsDiv.innerHTML = `
      <div style="margin-bottom: 0.5rem;">
        Required: ${requiredWidth}" × ${requiredHeight}" at ${requiredDPI} DPI<br>
        <small style="color: #999;">Minimum: ${Math.round(minWidth)} × ${Math.round(minHeight)} pixels</small>
      </div>
      <div style="color: ${meetsRequirements ? '#16a34a' : '#dc2626'}; font-weight: 600;">
        ${meetsRequirements ? '✓ Image meets requirements' : '⚠️ Image resolution too low'}
      </div>
    `;
    
    // Draw aspect ratio overlay
    if (meetsRequirements) {
      this.drawAspectRatioOverlay(requiredWidth, requiredHeight);
    }
  }

  drawAspectRatioOverlay(requiredWidth, requiredHeight) {
    // Draw a frame overlay showing the required aspect ratio for future crop feature
    const canvas = document.getElementById('viewer-overlay-canvas');
    const img = document.getElementById('viewer-image');
    
    if (!canvas || !img || !img.complete) return;
    
    const ctx = canvas.getContext('2d');
    const imgRect = img.getBoundingClientRect();
    const containerRect = img.parentElement.getBoundingClientRect();
    
    // Set canvas size to match image
    canvas.width = imgRect.width;
    canvas.height = imgRect.height;
    canvas.style.width = imgRect.width + 'px';
    canvas.style.height = imgRect.height + 'px';
    canvas.style.left = (imgRect.left - containerRect.left) + 'px';
    canvas.style.top = (imgRect.top - containerRect.top) + 'px';
    canvas.style.display = 'block';
    
    // Calculate required aspect ratio frame
    const requiredAspect = requiredWidth / requiredHeight;
    const imgAspect = img.naturalWidth / img.naturalHeight;
    
    let frameWidth, frameHeight;
    
    if (requiredAspect > imgAspect) {
      // Frame is wider - fit to width
      frameWidth = imgRect.width;
      frameHeight = frameWidth / requiredAspect;
    } else {
      // Frame is taller - fit to height
      frameHeight = imgRect.height;
      frameWidth = frameHeight * requiredAspect;
    }
    
    const frameX = (imgRect.width - frameWidth) / 2;
    const frameY = (imgRect.height - frameHeight) / 2;
    
    // Draw semi-transparent overlay outside the frame
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Clear the frame area
    ctx.clearRect(frameX, frameY, frameWidth, frameHeight);
    
    // Draw frame border
    ctx.strokeStyle = '#16a34a';
    ctx.lineWidth = 3;
    ctx.setLineDash([10, 5]);
    ctx.strokeRect(frameX, frameY, frameWidth, frameHeight);
    
    // Add label
    ctx.font = '14px sans-serif';
    ctx.fillStyle = '#16a34a';
    ctx.fillText(`Required aspect ratio: ${requiredWidth}:${requiredHeight}`, frameX + 10, frameY + 25);
  }

  /**
   * Get a user-friendly aspect ratio display string
   * Tries to match common ratios, falls back to simplified or decimal
   */
  getDisplayAspectRatio(width, height) {
    const ratio = width / height;
    
    // Common aspect ratios to match against (within 2% tolerance)
    const commonRatios = [
      { w: 1, h: 1, name: '1:1' },      // Square
      { w: 2, h: 1, name: '2:1' },      // Panoramic
      { w: 3, h: 1, name: '3:1' },      // Ultra-wide
      { w: 4, h: 3, name: '4:3' },      // Classic
      { w: 3, h: 2, name: '3:2' },      // 35mm film
      { w: 5, h: 4, name: '5:4' },      // Large format
      { w: 16, h: 9, name: '16:9' },    // Widescreen
      { w: 16, h: 10, name: '16:10' },  // Common monitor
      { w: 21, h: 9, name: '21:9' },    // Ultra-wide
      { w: 6, h: 3, name: '6:3' },      // Same as 2:1
      { w: 8, h: 4, name: '8:4' },      // Same as 2:1
      { w: 1, h: 2, name: '1:2' },      // Portrait panoramic
      { w: 3, h: 4, name: '3:4' },      // Portrait classic
      { w: 2, h: 3, name: '2:3' },      // Portrait 35mm
      { w: 9, h: 16, name: '9:16' },    // Portrait widescreen
    ];
    
    const tolerance = 0.02; // 2% tolerance
    
    for (const common of commonRatios) {
      const commonRatio = common.w / common.h;
      if (Math.abs(ratio - commonRatio) / commonRatio <= tolerance) {
        return common.name;
      }
    }
    
    // Try GCD simplification
    const gcd = this.getGCD(width, height);
    const aspectW = width / gcd;
    const aspectH = height / gcd;
    
    // If simplified ratio is reasonable (both under 50), use it
    if (aspectW <= 50 && aspectH <= 50) {
      return `${aspectW}:${aspectH}`;
    }
    
    // Fall back to decimal ratio
    return `${ratio.toFixed(2)}:1`;
  }

  getGCD(a, b) {
    return b === 0 ? a : this.getGCD(b, a % b);
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }
}

// Auto-initialize
new CartImageViewer();

